import { Event } from '../../../../common/models/event.interface';
import { EventOfferViewModel } from './event-offer';
import {
    convertDateToUTCTimestamp,
    convertUTCDateToTimezone
} from '../../../../common/utilities/dates';
import { EventState } from '../store/event.state';
import { cloneDeep } from 'lodash-es';
import { Pid } from '../../../../common/models/pid.interface';
import { FormGroup } from '@angular/forms';

export class EventViewModel {
    id: number;
    partnerEventCode: string;
    manufacturer: string;
    partner: string;
    gtins: any;
    pids: Partial<Pid>[];
    purchaseQty: number;
    matchedOffers: EventOfferViewModel[];
    event: Event;
    hasOffersDetails: boolean;
    linkedOfferCount: number;
    startDateLocal: Date;
    endDateLocal: Date;
    prodDateLocal: Date;
    lastUpdateDate: Date;

    constructor(event: Event, pidMap: EventState['data']['pids']) {
        if (event) {
            this.id = event.id; // needed for grid check box to work
            this.partnerEventCode = event.partner_event_code;
            this.event = event;
            this.pids = event.pids
                .map(pid => Number(pid.pid))
                .map(pidId => (pidMap[pidId] ? pidMap[pidId] : { pid: pidId }));
            this.gtins = event.gtins.map(gtin => gtin.gtin).join(', ');
            this.purchaseQty = event.purchase_qty;
            this.manufacturer = event.manufacturer
                ? event.manufacturer.name
                : '';
            this.partner = event.partner ? event.partner.name : '';
            this.matchedOffers = event.matched_offers.map(
                matchedOffer =>
                    new EventOfferViewModel(matchedOffer, this.event, pidMap)
            );

            this.hasOffersDetails =
                this.matchedOffers.length > 0 &&
                typeof this.matchedOffers[0].attribute === 'object';
            this.linkedOfferCount = this.matchedOffers.reduce(
                (sum, offer) => (offer.isLinked ? sum + 1 : sum),
                0
            );
            this.startDateLocal = convertUTCDateToTimezone(
                this.event.start_date,
                this.event.time_zone
            );
            this.endDateLocal = convertUTCDateToTimezone(
                this.event.end_date,
                this.event.time_zone
            );
            this.prodDateLocal = convertUTCDateToTimezone(
                this.event.offer_in_production_date,
                this.event.time_zone
            );
            this.lastUpdateDate = new Date(
                event.tracking_property.last_updated_date
            );
        }
    }

    patch(formGroup: FormGroup): Event {
        const patch = formGroup.value;
        const clone = this.event
            ? cloneDeep(this.event)
            : this.createNewEvent();

        for (const key in patch) {
            if (patch[key] instanceof Date) {
                // Convert dates to API format
                if (
                    key == 'start_date' &&
                    formGroup.controls['start_date'].dirty
                ) {
                    clone[key] = convertDateToUTCTimestamp(
                        patch.start_date,
                        patch.time_zone,
                        0,
                        0,
                        0
                    );
                } else if (
                    key == 'end_date' &&
                    formGroup.controls['end_date'].dirty
                ) {
                    clone[key] = convertDateToUTCTimestamp(
                        patch.end_date,
                        patch.time_zone,
                        23,
                        59,
                        59
                    );
                } else if (
                    key == 'offer_in_production_date' &&
                    formGroup.controls['offer_in_production_date'].dirty
                ) {
                    clone[key] = convertDateToUTCTimestamp(
                        patch.offer_in_production_date,
                        patch.time_zone,
                        23,
                        59,
                        59
                    );
                }
            } else if (key === 'pids') {
                clone[key] = patch.pids
                    ? patch.pids.map(pid => ({
                          pid: pid.pid
                      }))
                    : [];
            } else if (key === 'gtins') {
                clone[key] = patch.gtins
                    ? patch.gtins.split(',').map(gtin => ({
                          gtin: gtin.trim()
                      }))
                    : [];
            } else {
                clone[key] = patch[key];
            }
        }
        return clone;
    }

    createNewEvent(): Event {
        return {
            id: null,
            partner_event_code: null,
            name: null,
            purchase_qty: null,
            created_by: null,
            start_date: null,
            end_date: null,
            time_zone: null,
            brands: null,
            description: null,
            savings_type: null,
            partner: null,
            tracking_property: null
        };
    }
}
